// Дизайн-токены GetSlot, направление 01 «Поп». Единственный источник значений для web, tg и admin.
// Документация и правила: docs/design/system.md. После правки: pnpm --filter=@getslot/ui-tokens build:css

export type ThemeName = 'light'

export type ThemeColors = {
  bg: string
  surface: string
  well: string
  line: string
  lineStrong: string
  text: string
  muted: string
  subtle: string
  faint: string
  accent: string
  accentHover: string
  accentSoft: string
  onAccent: string
  onAccentSoft: string
  cta: string
  ctaHover: string
  onCta: string
  ink: string
  inkLine: string
  onInk: string
  onInkMuted: string
  peach: string
  onPeach: string
  peachText: string
  avatar: string
  success: string
  successSoft: string
  onSuccessSoft: string
  successDeep: string
  danger: string
  neutralSoft: string
  onNeutralSoft: string
  glass: string
  glassStrong: string
  ring: string
  focus: string
  live: string
}

const light: ThemeColors = {
  bg: '#F3F5F9',
  surface: '#FFFFFF',
  well: '#F3F5F9',
  line: '#EEF1F6',
  lineStrong: '#D5DBE6',
  text: '#14161A',
  muted: '#4A5060',
  subtle: '#6A7080',
  faint: '#AEB4C2',
  accent: '#2F6BFF',
  accentHover: '#1F4FD1',
  accentSoft: '#E3ECFF',
  onAccent: '#FFFFFF',
  onAccentSoft: '#1F4FD1',
  cta: '#FF7A21',
  ctaHover: '#F26A0F',
  onCta: '#14161A',
  ink: '#14161A',
  inkLine: '#33363D',
  onInk: '#FFFFFF',
  onInkMuted: '#C9CDD6',
  peach: '#FFE7D6',
  onPeach: '#8A3B00',
  peachText: '#5A2A00',
  avatar: '#FFD8BE',
  success: '#1B7A45',
  successSoft: '#DDF7E8',
  onSuccessSoft: '#135C33',
  successDeep: '#0F4D2B',
  danger: '#C4302B',
  neutralSoft: '#EEF1F6',
  onNeutralSoft: '#3C4150',
  glass: 'rgba(255, 255, 255, 0.18)',
  glassStrong: 'rgba(255, 255, 255, 0.35)',
  ring: 'rgba(255, 255, 255, 0.25)',
  focus: 'rgba(47, 107, 255, 0.45)',
  // точка «нашли дату» на синем фоне входа
  live: '#7CF2A7',
}

export const themes: Record<ThemeName, ThemeColors> = { light }

export const defaultTheme: ThemeName = 'light'

export const fonts = {
  body: '"Geologica Variable", Geologica, system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
  googleFamilies: 'family=Geologica:wght@400;500;600;700;800;900',
} as const

export const spacing = {
  x0: 2,
  x1: 4,
  x2: 8,
  x3: 12,
  x4: 16,
  x5: 20,
  x6: 24,
  x8: 32,
} as const

export const layout = {
  gutter: 16,
  headerX: 20,
  stackGap: 12,
  mobileWidth: 390,
  adminWidth: 1360,
  navHeight: 64,
  // Десктоп веба (app.getslot.ru вне Telegram). Граница брейкпоинта в медиазапросах пишется числом:
  // CSS-переменные там не работают, значение берётся отсюда.
  desktopMin: 1024,
  desktopWidth: 1120,
  desktopGutter: 40,
  formWidth: 640,
  sidebarWidth: 264,
  tileMin: 320,
  // лендинг getslot.ru
  landingWidth: 1200,
  landingGutter: 40,
  landingSection: 120,
} as const

export const radii = {
  bar: 3,
  tag: 10,
  cell: 14,
  field: 16,
  chip: 20,
  row: 22,
  card: 26,
  panel: 28,
  hero: 32,
  promo: 40,
  full: 9999,
} as const

export const sizes = {
  cta: 60,
  button: 52,
  field: 56,
  control: 44,
  chip: 40,
  progress: 6,
  liveDot: 8,
  navDot: 6,
  wordmark: 26,
  avatar: 40,
  avatarLarge: 64,
  iconTile: 40,
  actionCircle: 48,
  icon: 20,
  hairline: 1,
  border: 2,
  // лендинг: телефон в герое и самый большой круг радара
  phoneFrame: 320,
  phoneScreen: 560,
  radar: 900,
} as const

export const typography = {
  family: fonts.body,
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700, heavy: 800, black: 900 },
  styles: {
    hero: { size: 44, weight: 900, line: 0.96, tracking: '-0.045em' },
    // заголовок входа на десктопе
    heroWide: { size: 76, weight: 900, line: 0.94, tracking: '-0.05em' },
    // лендинг getslot.ru: заголовок героя, цена, заголовки секций
    promo: { size: 88, weight: 900, line: 0.95, tracking: '-0.045em' },
    promoPrice: { size: 96, weight: 900, line: 0.95, tracking: '-0.045em' },
    promoSection: { size: 56, weight: 900, line: 1, tracking: '-0.04em' },
    lead: { size: 20, weight: 500, line: 1.45, tracking: '0' },
    display: { size: 38, weight: 900, line: 1, tracking: '-0.04em' },
    title: { size: 32, weight: 900, line: 1, tracking: '-0.04em' },
    headline: { size: 26, weight: 900, line: 1.1, tracking: '-0.03em' },
    cardTitle: { size: 20, weight: 800, line: 1.2, tracking: '0' },
    button: { size: 18, weight: 800, line: 1, tracking: '0' },
    section: { size: 16, weight: 800, line: 1.3, tracking: '0' },
    body: { size: 15, weight: 500, line: 1.4, tracking: '0' },
    label: { size: 14, weight: 700, line: 1.3, tracking: '0' },
    meta: { size: 13, weight: 600, line: 1.3, tracking: '0' },
    caption: { size: 12, weight: 600, line: 1.3, tracking: '0' },
    nav: { size: 11, weight: 700, line: 1, tracking: '0' },
  },
} as const

export const shadows = {
  none: 'none',
  knob: '0 2px 4px rgba(0, 0, 0, 0.15)',
  lift: '0 12px 32px rgba(20, 22, 26, 0.10)',
  phone: '0 30px 80px rgba(20, 22, 26, 0.35)',
} as const

export const motion = {
  fastMs: 120,
  baseMs: 200,
  screenMs: 280,
  ease: 'cubic-bezier(0.2, 0.8, 0.3, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  livePulseMs: 1400,
  radarPingMs: 2400,
  confettiMs: 2000,
  spinMs: 900,
  pressScale: 0.97,
} as const

export const z = {
  nav: 10,
  sheet: 20,
  toast: 30,
} as const

export type StatusTone = 'search' | 'found' | 'booked' | 'client' | 'operator' | 'pause' | 'muted'

export const statusTones: Record<StatusTone, { bg: string; fg: string; dot?: string }> = {
  search: { bg: light.accentSoft, fg: light.onAccentSoft, dot: light.cta },
  found: { bg: light.accent, fg: light.onAccent, dot: light.cta },
  booked: { bg: light.successSoft, fg: light.onSuccessSoft },
  client: { bg: light.peach, fg: light.onPeach, dot: light.cta },
  operator: { bg: light.cta, fg: light.onCta },
  pause: { bg: light.neutralSoft, fg: light.onNeutralSoft },
  muted: { bg: light.neutralSoft, fg: light.muted },
}

export { brandColors, brandMark, brandWordmark } from './brand.ts'
